import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiClientError,
  createPoll,
  deletePoll,
  fetchPoll,
  finalizePoll,
  listHutDuties,
  listPolls,
  patchPoll,
  type HutDuty,
  type PollDetail,
  type PollStatus,
  type PollSummary,
} from '../../lib/api';
import { BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, INPUT, useErr } from './poll-ui';
import { ParticipantSection } from './PollParticipantSection';
import { SlotSection } from './PollSlotSection';

export function AdminDienstplanungTab() {
  const { t } = useTranslation();
  const toMsg = useErr();
  const [polls, setPolls] = useState<PollSummary[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const loadPolls = useCallback(async () => {
    try {
      const list = await listPolls();
      setPolls(list);
      setSelectedId(
        (cur) => cur ?? list.find((p) => p.status !== 'finalized')?.id ?? list[0]?.id ?? null,
      );
    } catch (e) {
      setError(toMsg(e));
    }
  }, [toMsg]);

  useEffect(() => {
    void loadPolls();
  }, [loadPolls]);

  const onCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      const p = await createPoll({ title: newTitle.trim() });
      setNewTitle('');
      setSelectedId(p.id);
      await loadPolls();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <p
          role="alert"
          className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600 dark:text-slate-400">
            {t('poll.admin.pollLabel')}
          </label>
          <select
            className={INPUT}
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t('poll.admin.none')}</option>
            {polls.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} · {t(`poll.status.${p.status}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            className={INPUT}
            placeholder={t('poll.admin.newTitlePlaceholder')}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <button type="button" className={BTN_PRIMARY} onClick={() => void onCreate()}>
            {t('poll.admin.createPoll')}
          </button>
        </div>
      </div>

      {selectedId !== null && (
        <PollManager
          key={selectedId}
          pollId={selectedId}
          onChanged={loadPolls}
          onDeleted={() => {
            setSelectedId(null);
            void loadPolls();
          }}
        />
      )}
    </div>
  );
}

/* ─── single-poll management ──────────────────────────────────────────── */

function PollManager({
  pollId,
  onChanged,
  onDeleted,
}: {
  pollId: number;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const toMsg = useErr();
  const [detail, setDetail] = useState<PollDetail | null>(null);
  const [roster, setRoster] = useState<HutDuty[]>([]);
  const [error, setError] = useState<string | null>(null);

  // listHutDuties() embeds every roster person's full slot history and is the
  // heaviest call on the page, so mutations that only touch the poll reload
  // just the poll.
  const reloadPoll = useCallback(async () => {
    try {
      setDetail(await fetchPoll(pollId));
    } catch (e) {
      setError(toMsg(e));
    }
  }, [pollId, toMsg]);

  const reloadAll = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([fetchPoll(pollId), listHutDuties()]);
      setDetail(d);
      setRoster(r);
    } catch (e) {
      setError(toMsg(e));
    }
  }, [pollId, toMsg]);

  useEffect(() => {
    void reloadAll();
  }, [reloadAll]);

  if (!detail) return <p className="text-sm text-slate-500">{error ?? '…'}</p>;

  const claimsBySlot = new Map<number, PollDetail['claims']>();
  for (const cl of detail.claims.filter((c) => c.status === 'active')) {
    const list = claimsBySlot.get(cl.slot_id) ?? [];
    list.push(cl);
    claimsBySlot.set(cl.slot_id, list);
  }
  const resolved = detail.slots.filter((s) => {
    if (s.state === 'closed') return true;
    if (s.state === 'locked') return s.assignee_name_id != null;
    return (claimsBySlot.get(s.id)?.length ?? 0) === 1;
  }).length;
  const contested = detail.slots.filter(
    (s) => s.state === 'open' && (claimsBySlot.get(s.id)?.length ?? 0) > 1,
  ).length;

  const setStatus = async (status: PollStatus) => {
    try {
      await patchPoll(pollId, { status });
      await reloadPoll();
      onChanged();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <p
          role="alert"
          className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}

      {/* header: status + deadline + intro */}
      <PollHeader
        detail={detail}
        onReload={reloadPoll}
        onChanged={onChanged}
        onDeleted={onDeleted}
      />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600 dark:text-slate-400">{t('poll.admin.statusFlow')}:</span>
        {(['draft', 'open', 'closed'] as PollStatus[]).map((s) => (
          <button
            key={s}
            type="button"
            className={detail.status === s ? BTN_PRIMARY : BTN_SECONDARY}
            onClick={() => void setStatus(s)}
          >
            {t(`poll.status.${s}`)}
          </button>
        ))}
        <span className="ml-auto text-slate-600 dark:text-slate-400">
          ✅ {resolved}/{detail.slots.length} · ⚠ {contested}
        </span>
      </div>

      {detail.status === 'draft' && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-300 dark:text-amber-300 dark:bg-amber-950 dark:border-amber-700 rounded-md px-3 py-2">
          {t('poll.admin.draftHint')}
        </p>
      )}

      <SlotSection detail={detail} roster={roster} onReload={reloadPoll} />
      <ParticipantSection detail={detail} roster={roster} onReload={reloadAll} />
      <FinalizeSection detail={detail} onReload={reloadPoll} onChanged={onChanged} />
    </div>
  );
}

function PollHeader({
  detail,
  onReload,
  onChanged,
  onDeleted,
}: {
  detail: PollDetail;
  onReload: () => Promise<void>;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const toMsg = useErr();
  const [deadline, setDeadline] = useState(detail.deadline ?? '');
  const [intro, setIntro] = useState(detail.intro_text ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    try {
      await patchPoll(detail.id, { deadline: deadline || null, intro_text: intro || null });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await onReload();
      onChanged();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(t('poll.admin.confirmDeletePoll'))) return;
    try {
      await deletePoll(detail.id);
      onDeleted();
    } catch (e) {
      setError(toMsg(e));
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold">{detail.title}</h2>
        <button type="button" className={BTN_DANGER} onClick={() => void remove()}>
          {t('poll.admin.deletePoll')}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-rose-700">
          {error}
        </p>
      )}
      <div className="grid sm:grid-cols-[auto,1fr] gap-3 items-start">
        <label className="text-sm text-slate-600 dark:text-slate-400 pt-2">
          {t('poll.admin.deadline')}
        </label>
        <input
          type="date"
          className={INPUT}
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
        />
        <label className="text-sm text-slate-600 dark:text-slate-400 pt-2">
          {t('poll.admin.introText')}
        </label>
        <textarea
          className={`${INPUT} min-h-[80px]`}
          value={intro}
          placeholder={t('poll.admin.introPlaceholder')}
          onChange={(e) => setIntro(e.target.value)}
        />
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className={BTN_PRIMARY} onClick={() => void save()}>
          {t('common.save')}
        </button>
        {saved && <span className="text-sm text-emerald-600">{t('poll.admin.saved')}</span>}
      </div>
    </div>
  );
}

/* ─── finalize ────────────────────────────────────────────────────────── */

function FinalizeSection({
  detail,
  onReload,
  onChanged,
}: {
  detail: PollDetail;
  onReload: () => Promise<void>;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const toMsg = useErr();
  const [syncNames, setSyncNames] = useState(false);
  const [writeClosed, setWriteClosed] = useState(true);
  const [result, setResult] = useState<string | null>(null);
  // Mail failures are reported beside the success line, not as an error: the
  // import still went through.
  const [notifyFailed, setNotifyFailed] = useState<{ email: string; error: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!window.confirm(t('poll.admin.confirmFinalize'))) return;
    setError(null);
    setResult(null);
    setNotifyFailed([]);
    try {
      const r = await finalizePoll(detail.id, {
        sync_names: syncNames,
        write_closed_availability: writeClosed,
      });
      setResult(
        t('poll.admin.finalizeDone', {
          imported: r.imported,
          overrides: r.closed_overrides,
          notified: r.notified,
        }),
      );
      setNotifyFailed(r.failed);
      await onReload();
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError && e.details && typeof e.details === 'object') {
        const problems =
          (e.details as { problems?: { label: string; reason: string }[] }).problems ?? [];
        setError(
          t('poll.admin.finalizeBlocked') +
            ' ' +
            problems
              .map(
                (p) => `${p.label} (${t(`poll.reason.${p.reason}`, { defaultValue: p.reason })})`,
              )
              .join(', '),
        );
      } else {
        setError(toMsg(e));
      }
    }
  };

  const disabled = detail.status === 'finalized';

  return (
    <section className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
      <h3 className="font-semibold">{t('poll.admin.finalize')}</h3>
      <p className="text-sm text-slate-600 dark:text-slate-400">{t('poll.admin.finalizeHelp')}</p>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={writeClosed}
          onChange={(e) => setWriteClosed(e.target.checked)}
        />
        {t('poll.admin.writeClosed')}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={syncNames}
          onChange={(e) => setSyncNames(e.target.checked)}
        />
        {t('poll.admin.syncNames')}
      </label>
      {error && (
        <p
          role="alert"
          className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
        >
          {error}
        </p>
      )}
      {result && <p className="text-sm text-emerald-600">{result}</p>}
      {notifyFailed.length > 0 && (
        <div className="text-xs text-rose-700 dark:text-rose-300">
          <p>{t('poll.admin.finalizeNotifyFailed')}</p>
          <ul>
            {notifyFailed.map((f, i) => (
              <li key={i}>
                {f.email ? `${f.email}: ` : ''}
                {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      <button type="button" className={BTN_PRIMARY} onClick={() => void run()} disabled={disabled}>
        {disabled ? t('poll.status.finalized') : t('poll.admin.finalizeButton')}
      </button>
    </section>
  );
}
